import { SPLIT_CONFIG_TYPES, AB_SPLIT_TEST_CONSTANTS } from '../core/constants';

const { EXPERIMENT_SPLIT, NORMAL_SPLIT, MULTI_SPLIT } = SPLIT_CONFIG_TYPES;
const {
	AB_SPLIT_TYPE: { CONTROL, EXPERIMENT }
} = AB_SPLIT_TEST_CONSTANTS;

/**
 * @name getControlAndExpSectionUnits
 * @description This Function iterates in units and provides the control & experiment section in case of EXPERIMENT_SPLIT
 * else provides the single control section in case of NORMAL_SPLIT
 * @param {object} units - Units Object
 * @param {string} videoSectionId - The Video Section Id
 * @returns {object} - {controlSection:{}, experimentSection:{}} will be returned in case of EXPERIMENT_SPLIT,
 * else in case of NORMAL_SPLIT only {controlSection:{}} will be returned
 */
export function getControlAndExpSectionUnits(units, videoSectionId) {
	for (const sectionRef in units) {
		const section = units[sectionRef];
		if (section.videoSectionId !== videoSectionId) continue;

		if (!section.abSplitTestData) {
			return { controlSection: section };
		}

		if (section.abSplitTestData.splitType === CONTROL) {
			return {
				controlSection: section,
				experimentSection: units[section.abSplitTestData.counterpartSectionRef]
			};
		}
	}
	return {};
}

export function getControlAndExpSectionInMultiSplit(units, videoSectionId) {
	let controlSection = null;
	const multiSplitSections = [];

	for (const sectionRef in units) {
		const section = units[sectionRef];
		if (section.videoSectionId !== videoSectionId || !section.multiSplitTestData) continue;

		if (section.multiSplitTestData.splitType === CONTROL) {
			controlSection = section;
		} else if (section.multiSplitTestData.splitType === EXPERIMENT) {
			multiSplitSections.push(section);
		}
	}

	return { controlSection, multiSplitSections };
}

export function getSplitsBasedOnSplitType(units, videoSectionId, splitType) {
	switch (splitType) {
		case EXPERIMENT_SPLIT: {
			return getControlAndExpSectionUnits(units, videoSectionId);
		}

		case MULTI_SPLIT: {
			return getControlAndExpSectionInMultiSplit(units, videoSectionId);
		}

		case NORMAL_SPLIT: {
			return getControlAndExpSectionUnits(units, videoSectionId);
		}

		default: {
			return {};
		}
	}
}

/**
 * @name getCounterpartSectionUnitElseNull
 * @description This function returns the name of the counterpart Control or Experiment section
 * @param {object} units - Units Object.
 * @param {string} sectionRef - The sectionRef Value of a Video Section.
 * @returns {string || null } Returns counterpartSection Unit if abSplitTestData exist, else null.
 */
export function getCounterpartSectionUnitElseNull(units, sectionRef) {
	// If Unit's abSplitTestData and counterpartSectionRef exists, then we return the counterpartSection Unit.
	if (
		units[sectionRef].abSplitTestData &&
		units[sectionRef].abSplitTestData.counterpartSectionRef
	) {
		const counterpartSectionRef = units[sectionRef].abSplitTestData.counterpartSectionRef;
		return units[counterpartSectionRef];
	}
	return null;
}

/**
 * @name getDataForSplitVisualizer
 * @description This function returns the data of all the section in an array of objects
 * with id, value and label properties.
 * @param {object} units - Units Object.
 * @param {object} sectionSplits - The sectionSplits øbject.
 * @returns {Array} data - The data of all the section in an array of objects.
 */
export function getDataForSplitVisualizer(units, sectionSplits) {
	const data = [];
	// Iterating in the sectionSplits Object to get the splitPercentage of each section
	for (const videoSectionId in sectionSplits) {
		// If the type of curr sectionSplit is EXPERIMENT_SPLIT, We'll extract & push
		// both control and experiment section Data.
		if (sectionSplits[videoSectionId].type === EXPERIMENT_SPLIT) {
			const controlExpSplitsObj = sectionSplits[videoSectionId].controlExperimentSplits;
			// Iterating & Adding {id, value, label} properties of control and experiment section
			for (const sectionRef in controlExpSplitsObj) {
				const splitPercentage = controlExpSplitsObj[sectionRef].splitPercentage;
				const sectionName = units[sectionRef].name;
				data.push({ id: sectionRef, value: splitPercentage, label: sectionName });
			}
		}
		if (sectionSplits[videoSectionId].type === NORMAL_SPLIT) {
			const splitPercentage = sectionSplits[videoSectionId].splitPercentage;
			const { controlSection } = getControlAndExpSectionUnits(units, videoSectionId);
			data.push({ id: videoSectionId, value: splitPercentage, label: controlSection.name });
		}
	}
	return data;
}

/**
 * @name checkIfNewSplitCanBeAdded
 * @description Checks various validation conditions to see if new Split can be added
 * @param {object} units - Units Object.
 * @param {string} selectedExistingUnitSectionRef - The selectedExistingUnitSectionRef.
 * @param {string} selectedSectionSplitType - The selectedSectionSplitType values : [NORMAL_SPLIT, EXPERIMENT_SPLIT].
 * @param {object} sectionLevelSplit - The sectionLevelSplit object.
 * @param {string} splitDescription - The splitDescription value.
 */
export function checkIfNewSplitCanBeAdded(
	units,
	selectedExistingUnitSectionRef,
	selectedSectionSplitType,
	sectionLevelSplit,
	splitDescription
) {
	if (!selectedExistingUnitSectionRef || !selectedSectionSplitType) {
		throw new Error('Select an existing unit and split type!');
	}
	if (
		selectedSectionSplitType === EXPERIMENT_SPLIT &&
		!units[selectedExistingUnitSectionRef].abSplitTestData
	) {
		throw new Error('Cannot Run Experiment Split on Section Without Ab Split Test Config!');
	}

	if (
		selectedSectionSplitType === MULTI_SPLIT &&
		!units[selectedExistingUnitSectionRef].multiSplitTestData
	) {
		throw new Error('Cannot Run Multi Split on Section Without multi Split Test Config!');
	}
	// checks if sectionId is already present in sectionLevelSplit
	const videoSectionId = units[selectedExistingUnitSectionRef].videoSectionId;
	const isVideoSectionIdPresentInSplits =
		!!sectionLevelSplit.splits && !!sectionLevelSplit.splits[videoSectionId];
	if (isVideoSectionIdPresentInSplits) {
		throw new Error('Section id already present in split config!');
	}

	if (selectedSectionSplitType === NORMAL_SPLIT && splitDescription === '') {
		throw new Error('Please enter a description for normal split!');
	}
}
